import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Anchor, Check } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { MEMBERSHIP_TIERS, MEMBER_CAP } from "../lib/captainsClub";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CaptainsClub() {
  const [selectedTier, setSelectedTier] = useState("captain");
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function submit() {
    if (!form.full_name || !form.email || !form.phone) {
      toast.error("Please fill in name, email, and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/membership/inquire`, {
        tier: selectedTier,
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        notes: form.notes,
      });
      setDone(true);
      toast.success("Your application is in — we'll be in touch shortly.");
    } catch (e) {
      toast.error("Could not submit application. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div data-testid="club-applied" className="bg-sand min-h-[80vh]">
        <section className="max-w-[900px] mx-auto px-6 lg:px-12 pt-28 pb-20 text-center">
          <div className="mx-auto w-14 h-14 bg-navy text-white flex items-center justify-center mb-8">
            <Check size={22} strokeWidth={1.5} />
          </div>
          <div className="overline mb-4">Application Received</div>
          <h1 className="font-serif-display text-5xl lg:text-6xl text-navy leading-tight">
            Welcome aboard, {form.full_name.split(" ")[0]}.
          </h1>
          <p className="text-[color:var(--am-muted)] max-w-xl mx-auto mt-6 leading-relaxed">
            Your application to join the Captain's Club is in. Our captain will personally review it
            and reach out within 48 hours with next steps — including a welcome packet and bank
            wire details for your first year's dues.
          </p>
          <Link to="/" className="btn-primary mt-10 inline-flex" data-testid="club-home-button">
            Back to Home <ArrowRight size={14} />
          </Link>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="captains-club-page" className="bg-sand">
      {/* HERO */}
      <section className="pt-28 pb-14 max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="overline mb-5">Est. 2026 · Bermuda</div>
        <h1 className="font-serif-display font-light text-5xl md:text-6xl lg:text-7xl text-navy leading-[1.02] max-w-4xl">
          The Captain's Club.
        </h1>
        <p className="mt-6 text-[color:var(--am-muted)] max-w-2xl leading-relaxed">
          A small, formal circle of members who prefer their days spent on the water.
          Three ranks. Lifetime friendships. A standing invitation to the sea. Families,
          friend groups, and corporates are warmly welcome — ask us about shared plans.
          Membership is capped at <strong className="text-navy">{MEMBER_CAP}</strong> to
          preserve access and intimacy.
        </p>
      </section>

      {/* TIERS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-20" data-testid="club-tiers">        <div className="overline mb-8">01 · The Ranks</div>
        <div className="grid md:grid-cols-3 gap-6">
          {MEMBERSHIP_TIERS.map((t, i) => {
            const isNavy = t.accent === "navy";
            const isPink = t.accent === "pink";
            return (
              <article
                key={t.id}
                onClick={() => setSelectedTier(t.id)}
                className={`relative p-10 flex flex-col cursor-pointer transition-all ${
                  isNavy
                    ? "bg-navy text-white"
                    : isPink
                    ? "bg-pink-soft text-navy"
                    : "bg-white text-navy border border-[color:var(--am-line)]"
                } ${
                  selectedTier === t.id ? "ring-2 ring-navy scale-[1.01]" : "hover:scale-[1.005]"
                }`}
                data-testid={`tier-${t.id}`}
              >
                {t.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-navy text-white text-[0.62rem] tracking-[0.24em] uppercase px-4 py-1">
                    Most Popular
                  </div>
                )}
                <Anchor
                  size={22}
                  strokeWidth={1.5}
                  className={isNavy ? "text-pink-soft" : "text-navy/40"}
                />
                <div
                  className={`text-[0.7rem] tracking-[0.22em] uppercase mt-8 mb-3 ${
                    isNavy ? "text-pink-soft" : "text-[color:var(--am-muted)]"
                  }`}
                >
                  0{i + 1} · Rank
                </div>
                <h2 className="font-serif-display text-5xl leading-none mb-2">{t.name}</h2>
                <p className={`italic text-sm mb-6 ${isNavy ? "text-white/70" : "text-[color:var(--am-muted)]"}`}>
                  {t.tagline}
                </p>
                <div className="font-serif-display text-4xl mb-1" data-testid={`tier-fee-${t.id}`}>
                  ${t.annualFee.toLocaleString()}
                </div>
                <div
                  className={`text-[0.68rem] tracking-[0.22em] uppercase mb-8 ${
                    isNavy ? "text-pink-soft" : "text-[color:var(--am-muted)]"
                  }`}
                >
                  per year
                </div>
                <ul className="space-y-3 flex-1">
                  {t.perks.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-sm leading-relaxed">
                      <Check
                        size={14}
                        strokeWidth={2}
                        className={`mt-1 shrink-0 ${isNavy ? "text-pink-soft" : "text-navy"}`}
                      />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                <div
                  className={`mt-8 pt-6 border-t text-[0.7rem] tracking-[0.22em] uppercase ${
                    isNavy ? "border-white/20 text-pink-soft" : "border-[color:var(--am-line)] text-[color:var(--am-muted)]"
                  }`}
                >
                  {selectedTier === t.id ? "Selected" : "Select this rank"}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* GROUPS & CORPORATE */}
      <section className="bg-pink-soft">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <div className="overline mb-4">Groups &amp; Corporate</div>
              <h2 className="font-serif-display text-4xl lg:text-5xl text-navy leading-tight">
                Bring your people aboard.
              </h2>
              <p className="text-navy/80 mt-5 leading-relaxed">
                Most of our members don't sail alone. Ask us about tailored memberships
                for your company, family, or friend group — shared perks, shared calendar,
                shared bragging rights.
              </p>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-3 gap-px bg-navy/10" data-testid="group-options">
              <div className="bg-pink-soft p-6" data-testid="group-corporate">
                <div className="overline mb-3">Corporate</div>
                <h3 className="font-serif-display text-2xl text-navy leading-tight mb-3">
                  Entertain clients in style.
                </h3>
                <p className="text-sm text-navy/70 leading-relaxed">
                  Company-wide access to the boat for client hospitality and team off-sites.
                  Up to <strong className="text-navy">8 named executives</strong> included.
                </p>
              </div>
              <div className="bg-pink-soft p-6" data-testid="group-family">
                <div className="overline mb-3">Family</div>
                <h3 className="font-serif-display text-2xl text-navy leading-tight mb-3">
                  One household, one membership.
                </h3>
                <p className="text-sm text-navy/70 leading-relaxed">
                  Extend any tier to cover your partner and children under one plan —
                  everyone books at member rates, kids sail free on birthdays.
                </p>
              </div>
              <div className="bg-pink-soft p-6" data-testid="group-friends">
                <div className="overline mb-3">Friends</div>
                <h3 className="font-serif-display text-2xl text-navy leading-tight mb-3">
                  Split a tier, share the sea.
                </h3>
                <p className="text-sm text-navy/70 leading-relaxed">
                  Up to <strong className="text-navy">4 friends</strong> can officially share a Captain or Commodore
                  tier — split the dues, take turns hosting, build a syndicate.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-4 sm:items-center">
            <a
              href="mailto:anchormgtbda@gmail.com?subject=Captain's%20Club%20Group%20Membership%20Inquiry"
              className="btn-primary inline-flex"
              data-testid="group-email-cta"
            >
              Email Us About Group Plans <ArrowRight size={14} />
            </a>
            <span className="text-xs text-navy/70">
              Or tell us in the application notes below and we'll tailor a proposal.
            </span>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white border-y border-[color:var(--am-line)]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-20">
          <div className="overline mb-4">02 · How to Join</div>
          <h2 className="font-serif-display text-4xl lg:text-5xl text-navy leading-tight max-w-3xl">
            Three steps to your year on the water.
          </h2>
          <div className="grid md:grid-cols-3 gap-10 mt-14">
            <div>
              <div className="font-serif-display text-5xl text-navy/30">01</div>
              <h3 className="font-serif-display text-2xl text-navy mt-3">Apply below</h3>
              <p className="text-sm text-[color:var(--am-muted)] mt-2 leading-relaxed">
                Choose a rank, share a few details, tell us about yourself.
              </p>
            </div>
            <div>
              <div className="font-serif-display text-5xl text-navy/30">02</div>
              <h3 className="font-serif-display text-2xl text-navy mt-3">Meet the captain</h3>
              <p className="text-sm text-[color:var(--am-muted)] mt-2 leading-relaxed">
                A quick call (or coffee, if you're on-island) so we can welcome you properly.
              </p>
            </div>
            <div>
              <div className="font-serif-display text-5xl text-navy/30">03</div>
              <h3 className="font-serif-display text-2xl text-navy mt-3">Wire your dues</h3>
              <p className="text-sm text-[color:var(--am-muted)] mt-2 leading-relaxed">
                Settle your first year with a bank wire. Your welcome packet arrives by post.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* APPLY FORM */}
      <section className="max-w-[900px] mx-auto px-6 lg:px-12 py-20" data-testid="club-apply">
        <div className="overline mb-4">03 · Apply for Membership</div>
        <h2 className="font-serif-display text-4xl lg:text-5xl text-navy leading-tight">
          Tell us about yourself.
        </h2>
        <p className="text-[color:var(--am-muted)] mt-4 leading-relaxed max-w-xl">
          You've selected <strong className="text-navy">{MEMBERSHIP_TIERS.find((t) => t.id === selectedTier)?.name}</strong>{" "}
          — <strong className="text-navy">${MEMBERSHIP_TIERS.find((t) => t.id === selectedTier)?.annualFee.toLocaleString()}/year</strong>.
          Tap a different rank above to change your selection.
        </p>

        <div className="mt-10 bg-white border border-[color:var(--am-line)] p-8 space-y-6">
          <div>
            <Label className="overline">Full Name</Label>
            <Input
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              placeholder="Jane Doe"
              className="mt-2 rounded-none"
              data-testid="club-name-input"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label className="overline">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="jane@example.com"
                className="mt-2 rounded-none"
                data-testid="club-email-input"
              />
            </div>
            <div>
              <Label className="overline">Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="441-555-0100"
                className="mt-2 rounded-none"
                data-testid="club-phone-input"
              />
            </div>
          </div>
          <div>
            <Label className="overline">A little about you (optional)</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="How did you hear about us? Any sailing background? Preferred dates?"
              rows={4}
              className="mt-2 rounded-none"
              data-testid="club-notes-input"
            />
          </div>
          <button
            onClick={submit}
            disabled={submitting}
            className="btn-primary w-full justify-center"
            data-testid="club-submit"
          >
            {submitting ? "Sending…" : "Submit Application"} <ArrowRight size={14} />
          </button>
        </div>
      </section>
    </div>
  );
}
